// =============================================================================
// Shared plan base for the HVAC set: model coordinates → sheet, the building
// drawn from the Revit model (walls cut at their openings, rooms with names and
// ceiling heights, columns, stairs, grids), and the HVAC symbology every sheet
// uses so plans, roof plan, riser diagram, details and legend agree.
//
// Model: Revit feet, X east, Y north. Sheet: points, y down, north up.
// =============================================================================
import { W, H, MARGIN, CONTENT_X, CONTENT_W, PT, INK, GRAY, MID, LIGHT, type Kit, type Box, boxOf, hits, knockText } from "./kit";
import type { Ctx } from "./ctx";
import { poleOf, inside, type Building, type P2, type Room } from "../model/building";
import type { BranchBox, Chase, ExhaustFan, IndoorUnit, OutdoorUnit, Rack, Run } from "../design/hvac";
import type { P3 } from "../model/schema";

export const PLAN = { x: CONTENT_X, y: MARGIN + 34, w: CONTENT_W, h: 1290 };
export const TABLES_Y = PLAN.y + PLAN.h + 76;
export const ARCH = "#9b9b9b", ARCH_FILL = "#d2d2d2", ARCH_TEXT = "#7a7a7a";

// ---- scales --------------------------------------------------------------------------------------
const STD_SCALES: [number, string][] = [[72, '1" = 1\'-0"'], [54, '3/4" = 1\'-0"'], [36, '1/2" = 1\'-0"'], [27, '3/8" = 1\'-0"'], [18, '1/4" = 1\'-0"'], [13.5, '3/16" = 1\'-0"'], [9, '1/8" = 1\'-0"'], [6.75, '3/32" = 1\'-0"'], [4.5, '1/16" = 1\'-0"'], [3, '1/24" = 1\'-0"']];
/** largest standard architectural scale (pt per ft = 72 × inches per foot) not over maxPtPerFt */
export const pickScale = (maxPtPerFt: number) => { for (const [ppf, label] of STD_SCALES) if (ppf <= maxPtPerFt) return { ppf, label }; return { ppf: maxPtPerFt, label: "NTS" }; };

export interface Mapper {
  ppf: number; label: string; bounds: { x0: number; y0: number; x1: number; y1: number }; area: typeof PLAN;
  X: (x: number) => number; Y: (y: number) => number; P: (p: P2 | P3) => { x: number; y: number };
}
/** a mapper that fits model bounds [x0..x1]×[y0..y1] (feet) into an area at a standard scale, north up */
export function fitMapper(bounds: { x0: number; y0: number; x1: number; y1: number }, area = PLAN, maxPpf = 72): Mapper {
  const bw = bounds.x1 - bounds.x0, bh = bounds.y1 - bounds.y0;
  const sc = pickScale(Math.min(area.w / bw, area.h / bh, maxPpf)); const ppf = sc.ppf;
  const ox = area.x + (area.w - bw * ppf) / 2, oy = area.y + (area.h - bh * ppf) / 2;
  const X = (x: number) => ox + (x - bounds.x0) * ppf, Y = (y: number) => oy + (bounds.y1 - y) * ppf;
  return { ppf, label: sc.label, bounds, area, X, Y, P: (p) => ({ x: X(p[0]), y: Y(p[1]) }) };
}
/** the whole building's footprint (all storeys, grids, racks, roof) — one frame for every plan so they stack */
export function buildingBounds(ctx: Ctx, margin = 6) {
  const { b, d } = ctx; const xs: number[] = [], ys: number[] = [];
  for (const r of b.rooms) for (const p of r.poly) { xs.push(p[0]); ys.push(p[1]); }
  for (const r of d.racks) for (const p of r.corners) { xs.push(p[0]); ys.push(p[1]); }
  for (const w of b.model.walls) if (w.bbox && w.bbox.max[2] > b.storeys[0].elev + 3) for (const p of w.centreline) { xs.push(p[0]); ys.push(p[1]); }
  return { x0: Math.min(...xs) - margin, y0: Math.min(...ys) - margin, x1: Math.max(...xs) + margin, y1: Math.max(...ys) + margin };
}
export const planMapper = (ctx: Ctx, area = PLAN) => fitMapper(buildingBounds(ctx), area);

// ---- grid references ------------------------------------------------------------------------------
/** "C/4" — the nearest lettered (east–west) and numbered (north–south) grid lines */
export function gridRef(b: Building, p: P2 | P3): string {
  let v = "", h = "", dv = Infinity, dh = Infinity;
  for (const g of b.model.grids) {
    const a = g.curve[0], c = g.curve[g.curve.length - 1]; const vert = Math.abs(a[0] - c[0]) < Math.abs(a[1] - c[1]);
    if (vert) { const dd = Math.abs(p[0] - a[0]); if (dd < dv) { dv = dd; v = g.name; } } else { const dd = Math.abs(p[1] - a[1]); if (dd < dh) { dh = dd; h = g.name; } }
  }
  return h && v ? `${h}/${v}` : h || v || "—";
}

// ---- the building ---------------------------------------------------------------------------------
export interface ArchOpts { roomNames?: boolean; ceilings?: boolean; grids?: boolean; tint?: (r: Room) => string | null }
/** walls cut at doors and windows, columns, stairs, rooms with names / numbers / ceiling heights, grids. Returns the label boxes it placed. */
export function drawArchitecture(ctx: Ctx, storey: number, M: Mapper, o: ArchOpts = {}): Box[] {
  const { k, b } = ctx; const m = b.model; const st = b.storeys[storey]; const taken: Box[] = [];
  const zMid = st.elev + 4;
  // room tints first, under everything
  if (o.tint) for (const r of st.rooms) { const c = o.tint(r); if (c) fillPoly(k, r.poly.map(M.P), c); }
  // walls
  const inserts = [...m.doors.map((x) => ({ x, door: true })), ...m.windows.map((x) => ({ x, door: false }))].filter((q) => q.x.location || q.x.bbox);
  for (const w of m.walls) {
    if (!w.bbox || w.bbox.min[2] > zMid || w.bbox.max[2] < zMid) continue;
    const mine = inserts.filter((q) => q.x.host === w.id && q.x.bbox && q.x.bbox.min[2] < st.top - 1 && q.x.bbox.max[2] > st.elev + 1);
    const pts = w.centreline;
    for (let i = 0; i + 1 < pts.length; i++) {
      const a: P2 = [pts[i][0], pts[i][1]], c: P2 = [pts[i + 1][0], pts[i + 1][1]]; const L = Math.hypot(c[0] - a[0], c[1] - a[1]); if (L < 0.02) continue;
      const t: P2 = [(c[0] - a[0]) / L, (c[1] - a[1]) / L]; const n: P2 = [-t[1], t[0]]; const hw = w.thicknessFt / 2;
      const ops = mine.flatMap(({ x, door }) => {
        const bb = x.bbox!; const loc = x.location ?? [(bb.min[0] + bb.max[0]) / 2, (bb.min[1] + bb.max[1]) / 2, 0];
        const s = (loc[0] - a[0]) * t[0] + (loc[1] - a[1]) * t[1]; const off = Math.abs((loc[0] - a[0]) * n[0] + (loc[1] - a[1]) * n[1]);
        const wd = x.widthFt ?? Math.abs((bb.max[0] - bb.min[0]) * t[0]) + Math.abs((bb.max[1] - bb.min[1]) * t[1]);
        if (off > w.thicknessFt + 1.5 || s < -wd / 2 || s > L + wd / 2) return [];
        return [{ s0: Math.max(0, s - wd / 2), s1: Math.min(L, s + wd / 2), door }];
      }).sort((p, q) => p.s0 - q.s0);
      const piece = (s0: number, s1: number) => { if (s1 - s0 < 0.02) return; const q = [[s0, -hw], [s1, -hw], [s1, hw], [s0, hw]].map(([s, e]) => M.P([a[0] + t[0] * s + n[0] * e, a[1] + t[1] * s + n[1] * e])); fillPoly(k, q, ARCH_FILL, ARCH, 0.35); };
      let s = 0;
      for (const op of ops) {
        if (op.s0 > s) piece(s, op.s0);
        if (!op.door) { // glazing: two thin lines in the opening
          for (const e of [-hw * 0.35, hw * 0.35]) { const p = M.P([a[0] + t[0] * op.s0 + n[0] * e, a[1] + t[1] * op.s0 + n[1] * e]), q = M.P([a[0] + t[0] * op.s1 + n[0] * e, a[1] + t[1] * op.s1 + n[1] * e]); k.rule(p.x, p.y, q.x, q.y, ARCH, 0.3); }
        }
        s = Math.max(s, op.s1);
      }
      if (s < L) piece(s, L);
    }
  }
  // columns and stairs
  for (const c of m.columns) if (c.bbox && c.bbox.min[2] < zMid && c.bbox.max[2] > zMid) { const p = M.P([c.bbox.min[0], c.bbox.max[1]]), q = M.P([c.bbox.max[0], c.bbox.min[1]]); k.rect(p.x, p.y, q.x - p.x, q.y - p.y, { fill: "#a9a9a9", w: 0.3, color: ARCH }); }
  for (const s of m.stairs) if (s.bbox && s.bbox.min[2] < st.elev + 2 && s.bbox.max[2] > st.elev + 2) {
    const p = M.P([s.bbox.min[0], s.bbox.max[1]]), q = M.P([s.bbox.max[0], s.bbox.min[1]]);
    k.rect(p.x, p.y, q.x - p.x, q.y - p.y, { w: 0.3, color: ARCH }); const n = Math.floor((q.y - p.y) / 7); for (let i = 1; i < n; i++) k.rule(p.x, p.y + i * 7, q.x, p.y + i * 7, LIGHT, 0.25);
  }
  // rooms: outline, name, number, ceiling height
  for (const r of st.rooms) {
    k.poly([...r.poly, r.poly[0]].map(M.P), LIGHT, 0.3);
    if (o.roomNames === false) continue;
    const at = M.P(poleOf(r.poly, r.holes, 1).p);
    const lines = [r.name.toUpperCase(), r.number];
    if (o.ceilings !== false && r.ceilingAboveLevelFt != null) lines.push(`CLG ${ftInS(r.ceilingAboveLevelFt)}${r.ceilingSource === "assumed" ? "*" : ""}`);
    let y = at.y - lines.length * 4.6;
    for (const [i, s] of lines.entries()) { const bx = boxOf(k, at.x, y, s, 6.2, "center"); k.text(at.x, y, s, { pt: 6.2, align: "center", color: ARCH_TEXT, bold: i === 0 }); taken.push(bx); y += 7.6; }
  }
  if (o.grids !== false) drawGrids(ctx, M);
  return taken;
}

export function drawGrids(ctx: Ctx, M: Mapper) {
  const { k, b } = ctx; const A = M.area;
  for (const g of b.model.grids) {
    const a = g.curve[0], c = g.curve[g.curve.length - 1]; const vert = Math.abs(a[0] - c[0]) < Math.abs(a[1] - c[1]);
    if (vert) { const x = M.X(a[0]); if (x < A.x + 10 || x > A.x + A.w - 10) continue; k.rule(x, A.y + 22, x, A.y + A.h - 22, MID, 0.3, [16, 4, 3, 4]); for (const y of [A.y + 12, A.y + A.h - 12]) { k.circle(x, y, 9, 0.5, "#FFFFFF", GRAY); k.text(x, y - 4.5, g.name, { pt: PT.tiny, align: "center", color: GRAY, bold: true }); } }
    else { const y = M.Y(a[1]); if (y < A.y + 10 || y > A.y + A.h - 10) continue; k.rule(A.x + 22, y, A.x + A.w - 22, y, MID, 0.3, [16, 4, 3, 4]); for (const x of [A.x + 12, A.x + A.w - 12]) { k.circle(x, y, 9, 0.5, "#FFFFFF", GRAY); k.text(x, y - 4.5, g.name, { pt: PT.tiny, align: "center", color: GRAY, bold: true }); } }
  }
}

/** key plan for the title strip: every storey's rooms in grey, the given storey darker */
export function keyPlan(ctx: Ctx, storey: number | null, x: number, y: number, w: number, h: number) {
  const { k, b } = ctx; const bb = buildingBounds(ctx, 1); const s = Math.min(w / (bb.x1 - bb.x0), h / (bb.y1 - bb.y0));
  const ox = x + (w - (bb.x1 - bb.x0) * s) / 2, oy = y + (h - (bb.y1 - bb.y0) * s) / 2;
  const P = (p: P2) => ({ x: ox + (p[0] - bb.x0) * s, y: oy + (bb.y1 - p[1]) * s });
  // outlines only, no fill: the ground floor faint for context, the sheet's storey (or the ground floor) in ink
  const ring = (pts: { x: number; y: number }[], c: string, lw: number) => { if (pts.length > 2) k.poly([...pts, pts[0]], c, lw); };
  const on = storey ?? 0; if (on !== 0) for (const r of b.storeys[0]?.rooms ?? []) ring(r.poly.map(P), LIGHT, 0.3);
  for (const r of b.storeys[on]?.rooms ?? []) ring(r.poly.map(P), INK, 0.35);
  k.tri([x + w - 6, y + 2], [x + w - 10, y + 12], [x + w - 2, y + 12]); k.text(x + w - 6, y + 13, "N", { pt: 5.5, align: "center" });
}

// ---- drawing helpers ------------------------------------------------------------------------------
export function fillPoly(k: Kit, pts: { x: number; y: number }[], fill: string, stroke?: string, w = 0.3) {
  if (pts.length < 3) return;
  k.doc.setFillColor(fill); k.doc.setDrawColor(stroke ?? fill); k.doc.setLineWidth(w);
  k.doc.lines(pts.slice(1).map((p, i) => [p.x - pts[i].x, p.y - pts[i].y]), pts[0].x, pts[0].y, [1, 1], stroke ? "FD" : "F", true);
}
export const ftInS = (ft: number) => { const f = Math.floor(ft + 1e-6); const i = Math.round((ft - f) * 12); return i === 12 ? `${f + 1}'-0"` : `${f}'-${i}"`; };

// ---- HVAC symbology (one source for plans, roof, riser, details, legend) ----------------------------
/** line styles: weight (pt) and dash */
export const STYLE = {
  indoor: { w: 0.9 },                  // line set to a cassette (liquid + suction, insulated, run together)
  trunk: { w: 1.8 },                   // main / branch line set (outdoor unit → branch joint → branch box)
  condensate: { w: 0.6, dash: [5, 2.5] },
  exhaust: { w: 0.45 },                // drawn double-line at its true 4" width + centre line
} as const;
export const styleOf = (r: Run) => r.kind === "condensate" ? STYLE.condensate : r.kind === "exhaust" ? STYLE.exhaust : r.role === "indoor" ? STYLE.indoor : STYLE.trunk;

/** 4-way cassette seen from below: panel square, grille square, four discharge slots */
export function cassetteGlyph(k: Kit, x: number, y: number, s: number) {
  k.rect(x - s / 2, y - s / 2, s, s, { fill: "#FFFFFF", w: 0.7 });
  const g = s * 0.36; k.rect(x - g / 2, y - g / 2, g, g, { w: 0.35 });
  const o = s * 0.4, l = s * 0.28; k.rule(x - l, y - o, x + l, y - o, INK, 0.9); k.rule(x - l, y + o, x + l, y + o, INK, 0.9); k.rule(x - o, y - l, x - o, y + l, INK, 0.9); k.rule(x + o, y - l, x + o, y + l, INK, 0.9);
}
/** branch box: rectangle with its ports as ticks on one side */
export function boxGlyph(k: Kit, x: number, y: number, w: number, h: number, ports = 3) {
  k.rect(x - w / 2, y - h / 2, w, h, { fill: "#FFFFFF", w: 0.8 }); k.rule(x - w / 2, y - h / 2, x + w / 2, y + h / 2, INK, 0.4);
  for (let i = 0; i < ports; i++) { const px = x - w / 2 + (w * (i + 1)) / (ports + 1); k.rule(px, y + h / 2, px, y + h / 2 + 3, INK, 0.6); }
}
/** outdoor unit in plan: casing with the fan circle; `along` true when the long side runs left–right on the sheet */
export function oduGlyph(k: Kit, x: number, y: number, wPt: number, dPt: number, longHoriz = true) {
  const w = longHoriz ? wPt : dPt, h = longHoriz ? dPt : wPt;
  k.rect(x - w / 2, y - h / 2, w, h, { fill: "#FFFFFF", w: 0.8 }); k.circle(x, y, Math.min(w, h) * 0.38, 0.5);
}
/** chase: hatched rectangle */
export function chaseGlyph(k: Kit, x: number, y: number, w: number, h: number) {
  k.rect(x - w / 2, y - h / 2, w, h, { fill: "#FFFFFF", w: 0.8 });
  k.doc.saveGraphicsState?.(); for (let t = -h; t < w; t += 3) { const x0 = x - w / 2 + Math.max(0, t), y0 = y + h / 2 - Math.max(0, -t); const len = Math.min(w - Math.max(0, t), h - Math.max(0, -t)); k.rule(x0, y0, x0 + len, y0 - len, GRAY, 0.25); } k.doc.restoreGraphicsState?.();
}
/** ceiling exhaust fan: square with an X */
export function fanGlyph(k: Kit, x: number, y: number, s: number) { k.rect(x - s / 2, y - s / 2, s, s, { fill: "#FFFFFF", w: 0.6 }); k.rule(x - s / 2, y - s / 2, x + s / 2, y + s / 2, INK, 0.4); k.rule(x - s / 2, y + s / 2, x + s / 2, y - s / 2, INK, 0.4); }
/** wall cap: a filled triangle pointing out of the wall */
export function capGlyph(k: Kit, x: number, y: number, dx: number, dy: number, s = 5) {
  const L = Math.hypot(dx, dy) || 1; const ux = dx / L, uy = dy / L;
  k.tri([x + ux * s, y + uy * s], [x - uy * s * 0.7, y + ux * s * 0.7], [x + uy * s * 0.7, y - ux * s * 0.7]);
}
/** a pipe leaving the floor: circle, arrow up or down */
export function riserGlyph(k: Kit, x: number, y: number, dir: "up" | "down", r = 4) {
  k.circle(x, y, r, 0.6, "#FFFFFF"); if (dir === "up") k.tri([x, y - r * 0.7], [x - r * 0.5, y + r * 0.35], [x + r * 0.5, y + r * 0.35]); else k.tri([x, y + r * 0.7], [x - r * 0.5, y - r * 0.35], [x + r * 0.5, y - r * 0.35]);
}
/** tag shapes: cassette CU rounded box, outdoor unit ODU hexagon, branch box BB diamond, chase CH box, fan EF circle, rack RK double box */
export function tag(k: Kit, x: number, y: number, s: string, kind: "CU" | "ODU" | "BB" | "CH" | "EF" | "RK" | "RL" = "CU", pt = PT.tiny): Box {
  const w = k.width(s, pt, true) + 8, h = k.lineH(pt) + 3;
  if (kind === "EF") { const r = Math.max(w, h) / 2; k.circle(x, y, r, 0.6, "#FFFFFF"); k.text(x, y - pt * 0.62, s, { pt, bold: true, align: "center" }); return { x0: x - r, y0: y - r, x1: x + r, y1: y + r }; }
  if (kind === "BB") { const a = w / 2 + 4, c = h / 2 + 2; k.doc.setFillColor("#FFFFFF"); k.doc.setDrawColor(INK); k.doc.setLineWidth(0.6); k.doc.lines([[a, -c], [a, c], [-a, c], [-a, -c]], x - a, y, [1, 1], "FD", true); /* left → top → right → bottom */ k.text(x, y - pt * 0.62, s, { pt, bold: true, align: "center" }); return { x0: x - w / 2 - 4, y0: y - h / 2, x1: x + w / 2 + 4, y1: y + h / 2 }; }
  if (kind === "ODU") { const r = h / 2; const pts = [[x - w / 2, y], [x - w / 2 + r, y - r], [x + w / 2 - r, y - r], [x + w / 2, y], [x + w / 2 - r, y + r], [x - w / 2 + r, y + r]]; k.doc.setFillColor("#FFFFFF"); k.doc.setDrawColor(INK); k.doc.setLineWidth(0.7); k.doc.lines(pts.slice(1).map((p, i) => [p[0] - pts[i][0], p[1] - pts[i][1]]), pts[0][0], pts[0][1], [1, 1], "FD", true); k.text(x, y - pt * 0.62, s, { pt, bold: true, align: "center" }); return { x0: x - w / 2, y0: y - r, x1: x + w / 2, y1: y + r }; }
  k.rect(x - w / 2, y - h / 2, w, h, { fill: "#FFFFFF", w: kind === "RK" ? 1.0 : 0.6 });
  if (kind === "RK") k.rect(x - w / 2 + 1.5, y - h / 2 + 1.5, w - 3, h - 3, { w: 0.35 });
  if (kind === "CU") { k.rule(x - w / 2, y - h / 2, x - w / 2 + 3, y - h / 2, "#FFFFFF", 1); }
  k.text(x, y - pt * 0.62, s, { pt, bold: true, align: "center" });
  return { x0: x - w / 2, y0: y - h / 2, x1: x + w / 2, y1: y + h / 2 };
}

/** the parts of a run that lie on a storey: horizontal segments within its height band, and where it leaves (up/down) */
export function runOnStorey(ctx: Ctx, r: Run, storey: number) {
  const st = ctx.b.storeys[storey]; const z0 = st.elev - 0.5, z1 = storey === ctx.b.storeys.length - 1 ? st.top + 1.5 : st.top - 0.05;
  const segs: [P3, P3][] = []; const risers: { at: P2; dir: "up" | "down" }[] = [];
  for (let i = 1; i < r.pts.length; i++) {
    const a = r.pts[i - 1], c = r.pts[i];
    const planLen = Math.hypot(c[0] - a[0], c[1] - a[1]), dz = Math.abs(a[2] - c[2]);
    const horiz = planLen > 0.02 && dz <= 0.1 * planLen + 0.05; // condensate falls 1 %: still horizontal
    if (horiz) { const z = (a[2] + c[2]) / 2; if (z >= z0 && z < z1) segs.push([a, c]); }
    else if (planLen <= 0.02) {
      const lo = Math.min(a[2], c[2]), hi = Math.max(a[2], c[2]);
      if (hi > z1 && lo < z1 && lo >= z0 - 0.5) risers.push({ at: [a[0], a[1]], dir: "up" });
      if (lo < z0 && hi >= z0 && hi < z1 + 0.5) risers.push({ at: [a[0], a[1]], dir: "down" });
    }
  }
  return { segs, risers };
}

/** draw a run's segments on a plan in its line style; exhaust drawn double-line at its real width */
export function drawRunSegs(k: Kit, M: Mapper, r: Run, segs: [P3, P3][], color = INK) {
  const st = styleOf(r);
  for (const [a, c] of segs) {
    const p = M.P(a), q = M.P(c);
    if (r.kind === "exhaust") {
      const L = Math.hypot(q.x - p.x, q.y - p.y) || 1; const nx = -(q.y - p.y) / L, ny = (q.x - p.x) / L; const hw = Math.max(2, (r.diaIn / 24) * M.ppf);
      k.rule(p.x + nx * hw, p.y + ny * hw, q.x + nx * hw, q.y + ny * hw, color, STYLE.exhaust.w); k.rule(p.x - nx * hw, p.y - ny * hw, q.x - nx * hw, q.y - ny * hw, color, STYLE.exhaust.w);
      k.rule(p.x, p.y, q.x, q.y, MID, 0.25, [6, 2, 1, 2]);
    } else k.rule(p.x, p.y, q.x, q.y, color, st.w, "dash" in st ? [...st.dash] : undefined);
  }
}

/** place a label near (x, y) at the first free candidate offset; returns its box or null */
export function placeLabel(k: Kit, x: number, y: number, s: string, taken: Box[], o: { pt?: number; bold?: boolean; cands?: [number, number][]; align?: "left" | "center" | "right" } = {}) {
  const pt = o.pt ?? PT.tiny; const cands = o.cands ?? [[6, -10], [6, 3], [-6, -10], [-6, 3], [0, -16], [0, 9], [10, -22], [10, 15]];
  for (const [dx, dy] of cands) {
    const align = o.align ?? (dx < 0 ? "right" : dx === 0 ? "center" : "left");
    const bx = boxOf(k, x + dx, y + dy, s, pt, align); if (hits(bx, taken)) continue;
    knockText(k, x + dx, y + dy, s, { pt, bold: o.bold, align }); taken.push(bx); return bx;
  }
  return null;
}

// ---- unit, shaft, flow and drainage symbology (added with the cassette / drainage / extract revision) -----------
export const STYLE2 = {
  condBranch: { w: 0.7, dash: [5, 2.5] },     // 3/4" condensate from a cassette
  condMain: { w: 1.3, dash: [9, 3] },          // condensate main to a riser
  condRiser: { w: 1.6 },                       // condensate riser / common drain (drawn with a riser glyph in plan)
  exhBranch: { w: 0.5 },                       // extract ducts: double line at true diameter
  exhMain: { w: 0.7 },
} as const;
export const styleOf2 = (r: Run) => r.kind === "condensate" ? (r.role === "main" ? STYLE2.condMain : r.role === "branch" ? STYLE2.condBranch : STYLE2.condRiser) : r.kind === "exhaust" ? (r.role === "main" ? STYLE2.exhMain : STYLE2.exhBranch) : styleOf(r);

/** an indoor unit seen from below at its true panel size (u.panel, plan feet): 4-way = four discharge slots with an
 *  arrow out of each side; 1-way = one slot on the discharge side with one arrow (u.blow, model directions, north up) */
export function unitGlyph(k: Kit, M: Mapper, u: IndoorUnit, o: { arrows?: boolean } = {}) {
  const c = M.P(u.pos); const w = u.panel[0] * M.ppf, h = u.panel[1] * M.ppf;
  k.rect(c.x - w / 2, c.y - h / 2, w, h, { fill: "#FFFFFF", w: 0.7 });
  if (u.type === "1-way") {
    const d = u.blow[0] ?? [1, 0]; const sx = d[0], sy = -d[1]; // sheet direction (y down)
    const g = Math.min(w, h) * 0.45; k.rect(c.x - g / 2, c.y - g / 2, g, g, { w: 0.35 }); // return grille
    // slot along the discharge edge
    if (Math.abs(sx) > 0.5) { const x = c.x + sx * (w / 2 - 2.2); k.rule(x, c.y - h * 0.36, x, c.y + h * 0.36, INK, 1.1); }
    else { const y = c.y + sy * (h / 2 - 2.2); k.rule(c.x - w * 0.36, y, c.x + w * 0.36, y, INK, 1.1); }
    if (o.arrows !== false) flowArrow(k, c.x + sx * (Math.abs(sx) > 0.5 ? w / 2 : 0), c.y + sy * (Math.abs(sy) > 0.5 ? h / 2 : 0), sx, sy, Math.max(9, Math.min(w, h) * 0.9));
    return;
  }
  const g = Math.min(w, h) * 0.36; k.rect(c.x - g / 2, c.y - g / 2, g, g, { w: 0.35 });
  const ox = w * 0.4, oy = h * 0.4, lx = w * 0.28, ly = h * 0.28;
  k.rule(c.x - lx, c.y - oy, c.x + lx, c.y - oy, INK, 0.9); k.rule(c.x - lx, c.y + oy, c.x + lx, c.y + oy, INK, 0.9);
  k.rule(c.x - ox, c.y - ly, c.x - ox, c.y + ly, INK, 0.9); k.rule(c.x + ox, c.y - ly, c.x + ox, c.y + ly, INK, 0.9);
  if (o.arrows !== false) { const L = Math.max(7, Math.min(w, h) * 0.45); flowArrow(k, c.x + w / 2, c.y, 1, 0, L); flowArrow(k, c.x - w / 2, c.y, -1, 0, L); flowArrow(k, c.x, c.y - h / 2, 0, -1, L); flowArrow(k, c.x, c.y + h / 2, 0, 1, L); }
}
/** an air / water flow arrow starting at (x, y), pointing along (dx, dy) in sheet directions, total length L */
export function flowArrow(k: Kit, x: number, y: number, dx: number, dy: number, L = 9, w = 0.6) {
  const n = Math.hypot(dx, dy) || 1; const ux = dx / n, uy = dy / n; const hl = Math.min(4, L * 0.45), hw = hl * 0.55;
  const tx = x + ux * L, ty = y + uy * L;
  k.rule(x + ux * 1, y + uy * 1, tx - ux * hl, ty - uy * hl, INK, w);
  k.tri([tx, ty], [tx - ux * hl - uy * hw, ty - uy * hl + ux * hw], [tx - ux * hl + uy * hw, ty - uy * hl - ux * hw]);
}
/** riser shaft in plan: the shaft outline (true size) with the standard diagonal cross */
export function shaftGlyph(k: Kit, M: Mapper, rect: { x0: number; y0: number; x1: number; y1: number }) {
  const p = M.P([rect.x0, rect.y1]), q = M.P([rect.x1, rect.y0]);
  k.rect(p.x, p.y, q.x - p.x, q.y - p.y, { fill: "#FFFFFF", w: 0.9 });
  k.rule(p.x, p.y, q.x, q.y, INK, 0.4); k.rule(p.x, q.y, q.x, p.y, INK, 0.4);
}
/** ceiling extract fan (as seen from below, true size): square casing, round grille, and the outlet spigot on the
 *  side its duct leaves (dx, dy model direction) — the duct is drawn from the spigot end */
export function fanGlyph2(k: Kit, M: Mapper, f: ExhaustFan, outlet?: P2) {
  const bb = f.bbox; const p = M.P([bb.min[0], bb.max[1]]), q = M.P([bb.max[0], bb.min[1]]);
  const w = Math.max(6, q.x - p.x), h = Math.max(6, q.y - p.y), cx = (p.x + q.x) / 2, cy = (p.y + q.y) / 2;
  k.rect(cx - w / 2, cy - h / 2, w, h, { fill: "#FFFFFF", w: 0.7 }); k.circle(cx, cy, Math.min(w, h) * 0.32, 0.45);
  k.rule(cx - Math.min(w, h) * 0.22, cy, cx + Math.min(w, h) * 0.22, cy, INK, 0.35); k.rule(cx, cy - Math.min(w, h) * 0.22, cx, cy + Math.min(w, h) * 0.22, INK, 0.35);
  if (outlet) { const o = M.P(outlet); k.rule(cx, cy, o.x, o.y, INK, 0.9); }
}
/** exhaust cap above the roof / wall cap: a mushroom (circle with a cross bar) */
export function roofCapGlyph(k: Kit, x: number, y: number, r = 5) { k.circle(x, y, r, 0.7, "#FFFFFF"); k.circle(x, y, r * 0.45, 0.5); k.rule(x - r * 1.3, y, x + r * 1.3, y, INK, 0.6); }
/** condensate outlet into the common drain: a trapped gully (square with a circle) */
export function gullyGlyph(k: Kit, x: number, y: number, s = 7) { k.rect(x - s / 2, y - s / 2, s, s, { fill: "#FFFFFF", w: 0.7 }); k.circle(x, y, s * 0.3, 0.5, INK); }

/** draw a run with the revision's styles (condensate and extract by role, extract double-line at true diameter) */
export function drawRunSegs2(k: Kit, M: Mapper, r: Run, segs: [P3, P3][], color = INK) {
  const st = styleOf2(r);
  for (const [a, c] of segs) {
    const p = M.P(a), q = M.P(c);
    if (r.kind === "exhaust") {
      const L = Math.hypot(q.x - p.x, q.y - p.y) || 1; const nx = -(q.y - p.y) / L, ny = (q.x - p.x) / L; const hw = Math.max(2, (r.diaIn / 24) * M.ppf);
      k.rule(p.x + nx * hw, p.y + ny * hw, q.x + nx * hw, q.y + ny * hw, color, st.w); k.rule(p.x - nx * hw, p.y - ny * hw, q.x - nx * hw, q.y - ny * hw, color, st.w);
      k.rule(p.x, p.y, q.x, q.y, MID, 0.25, [6, 2, 1, 2]);
    } else k.rule(p.x, p.y, q.x, q.y, color, st.w, "dash" in st ? [...(st as { dash: readonly number[] }).dash] : undefined);
  }
}

export { inside, W, H, MARGIN };
export type { BranchBox, Chase, ExhaustFan, IndoorUnit, OutdoorUnit, Rack, Run };
export type { Shaft, CondRiser, BomLine } from "../design/hvac";

/** (Revision 1) a branch-joint system's joint tree in words, from the design's data (o.jointTree / joints[].feeds), never assumed:
 *  "BJ-01 feeding BJ-02 and BJ-03 in parallel", "BJ-04 then BJ-05 in series", "BJ-08 only" */
export function jointTreeS(o: OutdoorUnit): string {
  const t = o.jointTree; const js = o.joints ?? [];
  const and = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
  if (!t) return js.length === 1 ? `${js[0].tag} only` : js.map((j) => j.tag).join(", ");
  if (t.topology === "single" || !t.links.length) return `${t.first} only`;
  const kids = (f: string) => t.links.filter((l) => l.from === f);
  const walk = (f: string): string => {
    const ks = kids(f); if (!ks.length) return f;
    if (ks.length === 1) { const chain = [f]; let c = ks[0].to; while (kids(c).length === 1) { chain.push(c); c = kids(c)[0].to; }
      return kids(c).length ? `${chain.join(" then ")} then ${walk(c)} in series` : `${[...chain, c].join(" then ")} in series`; }
    return `${f} feeding ${and(ks.map((l) => walk(l.to)))} in parallel`;
  };
  return walk(t.first);
}
